import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Type } from "lucide-react";
import SliderNav from "@/components/SliderNav";
import { supabase } from "@/integrations/supabase/client";
import ContactSheet from "@/components/ContactSheet";
import { testimonials as fallbackTestimonials } from "@/data/caseStudies";
import { Drawer, DrawerContent, DrawerClose } from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";

interface Review {
  id: string;
  name: string;
  company: string | null;
  testimonial: string;
  photo_url: string | null;
  review_date: string | null;
  type?: string;
}

const marqueeClients = [
  "WHOP", "EMPIRE", "EXCEPTIONAL 100", "C23 LABS",
  "CHILL BITES", "CONTRA", "BASE", "HUMMING.AI", "BROCKBERRY",
];

const TRUNCATE_LENGTH = 220;

const GrainOverlay = () => (
  <>
    <svg className="hidden">
      <filter id="grain-filter">
        <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
    </svg>
    <div
      className="absolute inset-0 pointer-events-none z-[1]"
      style={{
        filter: "url(#grain-filter)",
        opacity: 0.04,
      }}
    />
  </>
);

const ReviewContent = ({ review, useSans }: { review: Review; useSans?: boolean }) => (
  <div className="relative">
    <span
      className="block font-display italic text-foreground/[0.05] select-none pointer-events-none mb-2"
      style={{ fontSize: "80px", lineHeight: 0.8 }}
    >
      &ldquo;
    </span>

    <p className={`${useSans ? "font-body not-italic" : "font-display italic"} text-foreground leading-[1.7] text-[clamp(1rem,2vw,1.35rem)] -mt-6`}>
      &ldquo;{review.testimonial}&rdquo;
    </p>

    <div className="mt-8 flex items-baseline gap-3">
      <span className="font-body text-ts-xs font-medium uppercase tracking-[0.2em] text-foreground/80">
        {review.name}
      </span>
      {review.company && (
        <>
          <span className="text-foreground/20">—</span>
           <span className="font-body text-ts-xs uppercase tracking-[0.2em] text-muted-foreground">
            {review.company}
          </span>
        </>
      )}
    </div>
  </div>
);

const ReviewModal = ({
  review,
  onClose,
  useSans,
}: {
  review: Review;
  onClose: () => void;
  useSans?: boolean;
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25 }}
    className="fixed inset-0 z-50 flex items-center justify-center p-5 bg-background/80 backdrop-blur-sm"
    onClick={onClose}
  >
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.97 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="relative max-w-2xl w-full bg-background border border-foreground/[0.08] p-8 md:p-12 max-h-[80vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-1.5 text-muted-foreground/50 hover:text-foreground transition-colors"
        aria-label="Close"
      >
        <X size={16} />
      </button>
      <ReviewContent review={review} useSans={useSans} />
    </motion.div>
  </motion.div>
);

const TestimonialsSection = () => {
  const isMobile = useIsMobile();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeCategory, setActiveCategory] = useState<"client" | "freelancer">("client");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [expandedReview, setExpandedReview] = useState<Review | null>(null);
  const [useSans, setUseSans] = useState<boolean>(() => {
    try { return localStorage.getItem("testimonial-font") === "sans"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("testimonial-font", useSans ? "sans" : "serif"); } catch {}
  }, [useSans]);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*")
        .order("created_at", { ascending: false });

      if (error || !data || data.length === 0) {
        const mapped = fallbackTestimonials.map((t, i) => ({
          id: String(i),
          name: t.name,
          company: t.company || null,
          testimonial: t.text,
          photo_url: null,
          review_date: t.date,
        }));
        mapped.sort((a, b) => {
          if (!a.review_date) return 1;
          if (!b.review_date) return -1;
          return new Date(b.review_date).getTime() - new Date(a.review_date).getTime();
        });
        setReviews(mapped);
      } else {
        setReviews(data);
      }
    };
    fetchReviews();
  }, []);

  const clientReviews = reviews.filter((r) => (r.type || "client") === "client");
  const collabReviews = reviews.filter((r) => r.type === "freelancer");
  const filteredReviews = activeCategory === "client" ? clientReviews : collabReviews;

  useEffect(() => {
    setActiveIndex(0);
  }, [activeCategory]);

  // Auto-advance
  useEffect(() => {
    if (isPaused || filteredReviews.length <= 1 || expandedReview) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % filteredReviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isPaused, filteredReviews.length, expandedReview]);

  const goTo = useCallback((dir: -1 | 1) => {
    setActiveIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return filteredReviews.length - 1;
      return next % filteredReviews.length;
    });
  }, [filteredReviews.length]);

  const currentReview = filteredReviews[activeIndex];
  const marqueeText = marqueeClients.join("  ·  ");

  const isTruncated = currentReview && currentReview.testimonial.length > TRUNCATE_LENGTH;
  const displayText = currentReview
    ? isTruncated
      ? currentReview.testimonial.slice(0, TRUNCATE_LENGTH).trimEnd() + "…"
      : currentReview.testimonial
    : "";

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="section-light relative py-16 md:py-24 px-5 md:px-10 lg:px-14 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <GrainOverlay />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6 }}
        className="relative z-[2] flex flex-col md:flex-row md:items-baseline justify-between gap-4 mb-12 md:mb-16"
      >
        <div>
           <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
             06 — Kind Words
           </p>
          <h2 className="font-display text-4xl md:text-5xl italic text-foreground pt-1">
            Testimonials
          </h2>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setActiveCategory("client")}
            className={`font-body text-ts-2xs font-medium uppercase tracking-[0.15em] px-3.5 py-1.5 border transition-colors duration-300 ${
              activeCategory === "client"
                ? "border-foreground/30 text-foreground bg-foreground/[0.04]"
                : "border-foreground/[0.06] text-muted-foreground hover:text-foreground hover:border-foreground/15"
            }`}
          >
            Clients ({clientReviews.length})
          </button>
          <button
            onClick={() => setActiveCategory("freelancer")}
            className={`font-body text-ts-2xs font-medium uppercase tracking-[0.15em] px-3.5 py-1.5 border transition-colors duration-300 ${
              activeCategory === "freelancer"
                ? "border-foreground/30 text-foreground bg-foreground/[0.04]"
                : "border-foreground/[0.06] text-muted-foreground hover:text-foreground hover:border-foreground/15"
            }`}
          >
            Collaborators ({collabReviews.length})
          </button>

          <span className="w-px h-4 bg-foreground/10 mx-1.5" />

          <button
            onClick={() => setUseSans((v) => !v)}
            className={`p-1.5 transition-opacity duration-300 ${useSans ? "opacity-100" : "opacity-30 hover:opacity-60"}`}
            aria-label={useSans ? "Switch to serif font" : "Switch to sans-serif font"}
            title={useSans ? "Serif" : "Sans-serif"}
          >
            <Type size={14} />
          </button>
        </div>
      </motion.div>

      {/* Hero quote area — fixed height */}
      <div className="relative z-[2] min-h-[260px] md:min-h-[280px] flex flex-col justify-center">
        {/* Decorative open quote */}
        <span
          className="absolute -top-4 -left-2 md:-left-4 font-display italic text-foreground/[0.10] select-none pointer-events-none"
          style={{ fontSize: "clamp(100px, 15vw, 200px)", lineHeight: 1 }}
        >
          &ldquo;
        </span>

        <AnimatePresence mode="wait">
          {currentReview && (
            <motion.div
              key={currentReview.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="max-w-4xl"
            >
              <p
                className={`${useSans ? "font-body not-italic" : "font-display italic"} text-foreground leading-[1.6]`}
                style={{ fontSize: "clamp(1.1rem, 2.5vw, 1.8rem)" }}
              >
                &ldquo;{displayText}&rdquo;
                {isTruncated && (
                  <button
                    onClick={() => setExpandedReview(currentReview)}
                    className="inline ml-2 font-body not-italic text-ts-2xs uppercase tracking-[0.15em] text-primary/70 hover:text-primary transition-colors"
                  >
                    Read more
                  </button>
                )}
              </p>
              <div className="mt-6 flex items-baseline gap-3">
              <span className="font-body text-ts-xs font-medium uppercase tracking-[0.2em] text-foreground/80">
                  {currentReview.name}
                </span>
                {currentReview.company && (
                  <>
                    <span className="text-foreground/20">—</span>
                    <span className="font-body text-ts-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {currentReview.company}
                    </span>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {filteredReviews.length > 1 && (
          <div className="absolute bottom-0 left-0">
            <SliderNav
              activeIndex={activeIndex}
              total={filteredReviews.length}
              onNavigate={setActiveIndex}
              label="testimonial"
            />
          </div>
        )}
      </div>


      {/* CTA */}
      <div className="relative z-[2] mt-10 flex justify-end">
        <ContactSheet>
          <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground font-body text-ts-2xs font-medium tracking-[0.15em] uppercase hover:bg-primary/90 transition-colors">
            Start a Project
            <ArrowUpRight size={12} />
          </button>
        </ContactSheet>
      </div>

      {/* Full review — drawer on mobile, modal on desktop */}
      {isMobile ? (
        <Drawer open={!!expandedReview} onOpenChange={(open) => !open && setExpandedReview(null)}>
          <DrawerContent className="px-6 pb-8 pt-2 max-h-[85vh] overflow-y-auto">
            {expandedReview && <ReviewContent review={expandedReview} useSans={useSans} />}
          </DrawerContent>
        </Drawer>
      ) : (
        <AnimatePresence>
          {expandedReview && (
            <ReviewModal review={expandedReview} onClose={() => setExpandedReview(null)} useSans={useSans} />
          )}
        </AnimatePresence>
      )}
    </section>
  );
};

export default TestimonialsSection;
